import React from 'react';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { Card } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

interface CardHoverPreviewProps {
  hoveredCard: Card | null;
}

/**
 * CardHoverPreview — the single authoritative card preview surface for the
 * playtester. Oversized (340–400px wide), fixed to the right edge, vertically
 * centered. When a list panel (library, graveyard, exile, token, scry) is
 * open the preview sits on the LEFT edge instead, so it never covers the panel.
 *
 * Persistent preview (Shift+P / double-click on HandCard) takes priority over
 * hover state and always shows regardless of which panel is open.
 * The clear (✕) button dismisses the persistent preview.
 */
export default function CardHoverPreview({ hoveredCard }: CardHoverPreviewProps) {
  const persistentPreviewCard = usePlaytesterStore((s) => s.persistentPreviewCard);
  const setPersistentPreview = usePlaytesterStore((s) => s.setPersistentPreview);
  const openPanel = usePlaytesterStore((s) => s.openPanel);

  // When a list panel is open, only show persistent previews (hover would be
  // inside the panel overlay which already calls setHoveredCard). A persistent
  // card always shows.
  const effectiveHovered = openPanel ? null : hoveredCard;
  const card = persistentPreviewCard ?? effectiveHovered;
  if (!card) return null;

  // Prefer 'large' (672×936) for the oversized preview; falls back to 'normal'.
  const imageUri = getCardImageUri(card, 'large');
  if (!imageUri) return null;

  // When a panel is open the preview shifts to the LEFT so it doesn't cover the
  // panel modal. Otherwise it anchors to the RIGHT edge.
  const positionStyle: React.CSSProperties = openPanel
    ? { left: 12, top: '50%', transform: 'translateY(-50%)' }
    : { right: 12, top: '50%', transform: 'translateY(-50%)' };

  return (
    <div
      className="fixed z-[150] pointer-events-none"
      style={{ ...positionStyle, width: 360, maxHeight: '70vh' }}
    >
      <div className="relative">
        <img
          src={imageUri}
          alt={card.name}
          className="rounded-xl shadow-2xl border border-gray-700 w-full h-auto"
          style={{ borderRadius: CARD_CORNER_RADIUS,  maxHeight: '70vh', objectFit: 'contain' }}
          draggable={false}
        />
        {/* Persistent indicator — clear button */}
        {persistentPreviewCard && (
          <button
            className="absolute top-1 right-1 pointer-events-auto bg-black/70 hover:bg-black/90 text-gray-400 hover:text-white rounded-full w-6 h-6 flex items-center justify-center text-[11px] transition"
            onClick={() => setPersistentPreview(null)}
            title="Clear persistent preview (Shift+P)"
            aria-label="Clear persistent card preview"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

